import { motion } from 'motion/react';
import {
  ArrowRight,
  CheckCircle,
  Clock,
  CurrencyDollar,
  Headset,
  IdentificationCard,
  MapPin,
  Phone,
  ShieldCheck,
  WhatsappLogo,
} from '@phosphor-icons/react';
import { COMPANY } from '../lib/company';

const PILLARS = [
  {
    icon: MapPin,
    title: 'UAE-Based Office',
    body: 'We operate from Abu Dhabi, UAE. Walk in, call, or WhatsApp - you are always speaking to someone in the Emirates who understands local travel needs.',
    badge: 'Local team',
    tone: 'blue',
  },
  {
    icon: IdentificationCard,
    title: 'Visa & Document Experts',
    body: 'Our consultants handle Schengen, UK, USA, Canada, Australia and UAE visit visas every day. We review every document before submission.',
    badge: 'Document review',
    tone: 'sky',
  },
  {
    icon: CurrencyDollar,
    title: 'Transparent Quotations',
    body: 'You receive a clear, itemised quote before paying anything. No hidden fees added at checkout - the price we quote is the price you pay.',
    badge: 'Clear pricing',
    tone: 'green',
  },
  {
    icon: WhatsappLogo,
    title: 'WhatsApp Support',
    body: 'Chat directly with your consultant on WhatsApp from enquiry to return. Urgent support is available 24/7.',
    badge: 'Fast replies',
    tone: 'emerald',
  },
  {
    icon: Headset,
    title: 'Support During Travel',
    body: 'Our team remains reachable throughout your trip. If anything changes - flight delays, hotel issues, visa queries - we are one message away.',
    badge: 'Trip support',
    tone: 'indigo',
  },
  {
    icon: ShieldCheck,
    title: 'Licensed Travel Agency',
    body: `UAE-registered travel agency. Trade License: ${COMPANY.registration.tradeLicense}.`,
    badge: 'Licensed',
    tone: 'navy',
  },
];

const ASSURANCES = [
  'No payment required to make an enquiry',
  'Custom quotation with full cost breakdown',
  'UAE-based consultant assigned to your booking',
  'Response within 1 hour',
];

const TONES: Record<string, { bg: string; text: string; icon: string; badge: string }> = {
  blue: { bg: '#EAF7FF', text: '#0077CC', icon: '#0096FF', badge: 'bg-[#EAF7FF] text-[#006ED3]' },
  sky: { bg: '#EEF8FF', text: '#0369A1', icon: '#0EA5E9', badge: 'bg-[#EEF8FF] text-[#0369A1]' },
  green: { bg: '#EAFBF1', text: '#0C8A45', icon: '#16B85E', badge: 'bg-[#EAFBF1] text-[#0C8A45]' },
  emerald: { bg: '#E9FBF3', text: '#087A52', icon: '#25D366', badge: 'bg-[#E9FBF3] text-[#087A52]' },
  indigo: { bg: '#EEF2FF', text: '#4338CA', icon: '#4F46E5', badge: 'bg-[#EEF2FF] text-[#4338CA]' },
  navy: { bg: '#EDF4FF', text: '#0B3D75', icon: '#0B3D75', badge: 'bg-[#EDF4FF] text-[#0B3D75]' },
};

interface TrustSignalsProps {
  bg?: string;
}

export default function TrustSignals({ bg = 'bg-[#F5FBFF]' }: TrustSignalsProps) {
  return (
    <section className={`relative overflow-hidden ${bg} px-4 py-10 sm:px-6 lg:px-8 lg:py-12`}>
      <img
        src="/assets/backgrounds/decorative_flight_path_illustration.webp"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -left-28 top-2 hidden w-[460px] opacity-[0.1] lg:block" loading="lazy" />
      <img
        src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40 w-full object-cover object-bottom opacity-[0.11]" loading="lazy" />
      <img
        src="/assets/backgrounds/soft_blue_abstract_splash_shape.webp"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-36 -top-28 hidden w-[480px] opacity-[0.16] xl:block" loading="lazy" />

      <div className="relative z-10 mx-auto max-w-[1420px]">
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.34em] text-[#0068B3]">
              Why Trust Us
            </span>
            <h2
              className="font-display font-black leading-tight text-[#071126]"
              style={{ fontSize: 'clamp(1.9rem, 3.1vw, 3rem)' }}
            >
              Your UAE-Based Travel Partner
            </h2>
            <div className="mt-3 h-1 w-16 rounded-full bg-[#0096FF]" />
          </div>
          <p className="max-w-2xl text-[14px] font-semibold leading-relaxed text-[#40516A] md:text-[15px]">
            Big Bird Travel helps UAE residents plan holidays, apply for visas and perform Umrah across the Emirates.
            We are a real team, in a real office, with real accountability.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            const tone = TONES[p.tone] ?? TONES.blue;

            return (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group flex min-h-[154px] gap-4 rounded-[22px] border border-[#D7E9F8] bg-white/95 p-5 shadow-[0_14px_34px_rgba(7,17,38,0.07)] transition-all duration-300 hover:-translate-y-1 hover:border-[#0096FF]/45 hover:shadow-[0_22px_50px_rgba(0,150,255,0.13)]"
              >
                <span
                  className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-[18px] ring-1 ring-black/5"
                  style={{ backgroundColor: tone.bg, color: tone.icon }}
                >
                  <Icon size={30} weight="duotone" />
                </span>
                <span className="min-w-0">
                  <span className={`mb-2 inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${tone.badge}`}>
                    {p.badge}
                  </span>
                  <h3 className="font-display text-[18px] font-black leading-tight text-[#071126]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[13px] font-medium leading-relaxed text-[#40516A]">
                    {p.body}
                  </p>
                </span>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-5 overflow-hidden rounded-[24px] border border-[#BFE1FF] bg-white/90 shadow-[0_18px_48px_rgba(7,17,38,0.1)] backdrop-blur">
          <div className="grid gap-0 lg:grid-cols-[1fr_auto]">
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 xl:grid-cols-4">
              {ASSURANCES.map((a, idx) => (
                <div
                  key={a}
                  className={`flex items-center gap-3 px-5 py-4 ${idx > 0 ? 'xl:border-l xl:border-[#D7E9F8]' : ''} ${idx > 1 ? 'sm:border-t sm:border-[#D7E9F8] xl:border-t-0' : ''}`}
                >
                  <CheckCircle size={18} weight="fill" className="shrink-0 text-[#0068B3]" />
                  <span className="text-[13px] font-black leading-snug text-[#071126]">{a}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 border-t border-[#D7E9F8] bg-[#F4FAFF] p-4 sm:flex-row lg:border-l lg:border-t-0">
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=Hi%20Big%20Bird%20Travel%2C%20I%20have%20an%20enquiry.`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#0F7A35] px-5 text-[13px] font-black text-white shadow-lg shadow-[#25D366]/20 transition-all hover:bg-[#20ba5a]"
              >
                <WhatsappLogo size={18} weight="fill" /> WhatsApp Us <ArrowRight size={16} weight="bold" />
              </a>
              <a
                href={`tel:${COMPANY.phone}`}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-[#BFE1FF] bg-white px-5 text-[13px] font-black text-[#071126] transition-all hover:border-[#0096FF] hover:text-[#0068B3]"
              >
                <Phone size={17} weight="fill" /> {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 border-t border-[#D7E9F8] px-4 py-3 text-center text-[12px] font-bold text-[#5B6B82]">
            <Clock size={14} weight="duotone" className="text-[#0068B3]" />
            WhatsApp support available 7 days a week
          </div>
        </div>
      </div>
    </section>
  );
}
